// The OT dashboard: how many cases are at each stage, what is booked today,
// and how many theatres are in service.

import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Box, Typography } from "@mui/material";

import { IPD_COLORS, IpdDataGrid, IpdStatCard, IpdSectionCard } from "../../ipd/utils";
import EditNoteIcon from "@mui/icons-material/EditNote";
import EventIcon from "@mui/icons-material/Event";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import { formatIstTime } from "../../../utils/istDateTime";
import { fetchDashboard } from "../api/otApi";
import OtScreen from "../components/OtScreen";
import RequestQueue from "../components/RequestQueue";
import { FirmnessChip, LifecycleChip, errorMessage } from "../components/common";
import { FIRMNESS, OT_BASE } from "../constants";

/**
 * The day's list broken down by how firm each case is (OT_0022).
 *
 * One bar, not seven tiles. Firmness is a share of a single total, and the
 * question it answers — "how much of today is actually settled" — is a
 * proportion. Seven separate numbers make the reader do that arithmetic.
 *
 * Empty states are dropped from the legend rather than shown as zero: a
 * firmness with no cases in it is not a finding.
 */
function FirmnessBar({ byFirmness }) {
  const rows = Object.keys(FIRMNESS)
    .map((key) => ({ key, ...FIRMNESS[key], value: byFirmness[key] ?? 0 }))
    .filter((r) => r.value > 0);
  const total = rows.reduce((sum, r) => sum + r.value, 0);

  if (!total) {
    return (
      <Typography sx={{ fontSize: 13.5, color: IPD_COLORS.textSecondary }}>
        No cases on the list yet.
      </Typography>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: "flex",
          height: 10,
          borderRadius: 999,
          overflow: "hidden",
          border: `1px solid ${IPD_COLORS.border}`,
          mb: 1.5,
        }}
      >
        {rows.map((r) => (
          <Box
            key={r.key}
            title={`${r.label}: ${r.value}`}
            sx={{ width: `${(r.value / total) * 100}%`, bgcolor: r.color }}
          />
        ))}
      </Box>
      <Box sx={{ display: "flex", gap: 2.5, flexWrap: "wrap" }}>
        {rows.map((r) => (
          <Box key={r.key} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: r.color }} />
            <Typography sx={{ fontSize: 12.5, color: IPD_COLORS.textSecondary }}>
              {r.label}
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: IPD_COLORS.textPrimary }}>
              {r.value}
            </Typography>
          </Box>
        ))}
      </Box>
    </>
  );
}

export default function OtDashboard() {
  const history = useHistory();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setData(await fetchDashboard());
    } catch (err) {
      setError(errorMessage(err, "Could not load the OT dashboard"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const counts = data?.case_counts || {};
  const byFirmness = counts.by_firmness || {};

  const columns = [
    {
      field: "time",
      headerName: "Time",
      width: 110,
      valueGetter: (p) => p.row.start_datetime,
      renderCell: (p) => (
        <span>
          {formatIstTime(p.row.start_datetime)} – {formatIstTime(p.row.end_datetime)}
        </span>
      ),
    },
    { field: "operating_room_name", headerName: "Theatre", width: 140 },
    {
      field: "patient",
      headerName: "Patient",
      flex: 1,
      minWidth: 180,
      valueGetter: (p) => p.row.ot_case?.patient_name || "—",
    },
    {
      field: "surgeon",
      headerName: "Surgeon",
      width: 160,
      valueGetter: (p) => p.row.ot_case?.primary_surgeon_name || "—",
    },
    {
      field: "type",
      headerName: "Type",
      width: 110,
      valueGetter: (p) => p.row.ot_case?.scheduling_type || "—",
    },
    {
      field: "status",
      headerName: "Status",
      width: 130,
      sortable: false,
      renderCell: (p) => <LifecycleChip status={p.row.ot_case?.lifecycle_status} />,
    },
    {
      field: "firmness",
      headerName: "List",
      width: 120,
      sortable: false,
      renderCell: (p) => <FirmnessChip firmness={p.row.ot_case?.firmness} />,
    },
  ];

  return (
    <OtScreen
      title="OT Dashboard"
      description="Today's theatre activity at a glance."
      loading={loading}
      error={error}
      onRetry={load}
    >
      {/* Above the numbers on purpose: an unbooked request is the only
          thing on this screen that nobody else is going to pick up. */}
      <RequestQueue onScheduled={load} />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
          gap: 2,
          mb: 2.5,
        }}
      >
        <IpdStatCard
          label="Draft"
          value={counts.draft ?? 0}
          icon={EditNoteIcon}
          loading={loading}
        />
        <IpdStatCard
          label="Scheduled"
          value={counts.scheduled ?? 0}
          icon={EventIcon}
          tone="blue"
          loading={loading}
        />
        <IpdStatCard
          label="Approved / Listed"
          value={counts.approved_or_listed ?? 0}
          icon={PlaylistAddCheckIcon}
          tone="mint"
          loading={loading}
        />
        <IpdStatCard
          label="Theatres in service"
          value={data?.active_room_count ?? 0}
          icon={MeetingRoomIcon}
          loading={loading}
        />
      </Box>

      <IpdSectionCard title="List firmness" sx={{ mb: 2.5 }}>
        <FirmnessBar byFirmness={byFirmness} />
      </IpdSectionCard>

      <IpdSectionCard
        title={`Today's theatre list (${data?.todays_booking_count ?? 0})`}
        subtitle="Click a row to open the case."
      >
        <IpdDataGrid
          rows={data?.todays_bookings || []}
          columns={columns}
          getRowId={(row) => row.id}
          embedded
          emptyMessage="Nothing booked for today"
          emptyDescription="Bookings made for today appear here."
          onRowClick={(p) => history.push(`${OT_BASE}/case/${p.row.ot_case?.id}`)}
          sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
        />
      </IpdSectionCard>

    </OtScreen>
  );
}
